<script setup lang="ts">
import { useRouter } from 'vue-router'
import SourceButton from '~/components/ui/SourceButton.vue'
import { useSourceToast } from '~/composables/ui/useSourceToast'
import { performAppSessionReset } from '~~/utils/clientSessionReset'

const props = withDefaults(
  defineProps<{
    title: string
    description?: string
    illustrationUrl: string
    /** Affiche le lien texte « Retourner à la page précédente » */
    showBackLink?: boolean
    showHomeButton?: boolean
    /** Déconnexion OIDC + suppression des cookies applicatifs du domaine courant */
    showSessionReset?: boolean
    /** Hauteur minimale type viewport (page pleine) */
    fullHeight?: boolean
    /**
     * Sur `error.vue` Nuxt : appeler `clearError()` avant navigation pour sortir de la boundary.
     */
    clearErrorBoundary?: boolean
  }>(),
  {
    description: '',
    showBackLink: true,
    showHomeButton: true,
    showSessionReset: false,
    fullHeight: true,
    clearErrorBoundary: false,
  },
)

const router = useRouter()
const toast = useSourceToast()
const { loggedIn, logout } = useOidcAuth()

const canGoBack = computed(() => {
  if (!import.meta.client) {
    return false
  }
  return globalThis.history.length > 1
})

function maybeClearNuxtErrorBoundary(): void {
  if (!props.clearErrorBoundary) {
    return
  }
  void clearError()
}

function onBack(): void {
  maybeClearNuxtErrorBoundary()
  if (canGoBack.value) {
    router.back()
    return
  }
  void navigateTo({ path: '/' })
}

function onHome(): void {
  maybeClearNuxtErrorBoundary()
  void navigateTo({ path: '/' })
}

async function onSessionReset(): Promise<void> {
  await performAppSessionReset({
    loggedIn: loggedIn.value,
    logout,
    toast,
    beforeNavigate: maybeClearNuxtErrorBoundary,
  })
}
</script>

<template>
  <div class="app-error-screen" :class="{ 'app-error-screen--full': fullHeight }" role="alert">
    <div class="app-error-screen__card">
      <div class="app-error-screen__art">
        <img
          class="app-error-screen__img"
          :src="illustrationUrl"
          alt=""
          width="280"
          height="210"
          decoding="async"
        >
      </div>
      <div class="app-error-screen__body">
        <h1 class="app-error-screen__title">{{ title }}</h1>
        <p v-if="description" class="app-error-screen__desc">{{ description }}</p>

        <div class="app-error-screen__links">
          <button
            v-if="showBackLink"
            type="button"
            class="app-error-screen__text-link"
            @click="onBack"
          >
            Retourner à la page précédente
          </button>
        </div>

        <div v-if="showHomeButton || showSessionReset" class="app-error-screen__actions">
          <SourceButton
            v-if="showHomeButton"
            type="secondary"
            size="small"
            text="Accueil"
            icon="home"
            @click="onHome"
          />
          <SourceButton
            v-if="showSessionReset"
            type="tertiary"
            size="small"
            text="Déconnexion et cookies"
            icon="logout"
            @click="onSessionReset"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/spacing' as *;
@use '~/assets/scss/partials/elpdl/colors' as c;

.app-error-screen {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-xl;
  box-sizing: border-box;
  width: 100%;
  background: c.$enedis-neutral-30;
}

.app-error-screen--full {
  min-height: min(70dvh, 32rem);
}

.app-error-screen__card {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: $spacing-2xl;
  max-width: 56rem;
  width: 100%;
  padding: $spacing-2xl;
  background: c.$enedis-neutral-10;
  border-radius: 12px;
  box-shadow: var(--color-shadow-modal); /* elevation-300 */
  border: 1px solid c.$enedis-neutral-40;
}

.app-error-screen__art {
  flex: 0 0 auto;
  max-width: 45%;
}

.app-error-screen__img {
  display: block;
  width: 100%;
  height: auto;
  max-width: 280px;
}

.app-error-screen__body {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
}

.app-error-screen__title {
  margin: 0 0 $spacing-s 0;
  font-size: clamp(1.35rem, 2.5vw, 1.75rem);
  font-weight: 700;
  color: c.$enedis-neutral-900;
  line-height: 1.2;
}

.app-error-screen__desc {
  margin: 0 0 $spacing-m 0;
  font-size: 1rem;
  line-height: 1.5;
  color: c.$enedis-neutral-700;
}

.app-error-screen__links {
  margin-bottom: $spacing-m;
}

.app-error-screen__text-link {
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  color: c.$enedis-primary-600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.app-error-screen__text-link:hover {
  color: c.$enedis-primary-700;
}

.app-error-screen__text-link:focus-visible {
  outline: 2px solid c.$enedis-primary-500;
  outline-offset: 2px;
  border-radius: 4px;
}

.app-error-screen__actions {
  display: flex;
  flex-wrap: wrap;
  gap: $spacing-s;
  align-items: center;
}

@media (max-width: 720px) {
  .app-error-screen__card {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    padding: $spacing-xl;
  }

  .app-error-screen__art {
    max-width: 100%;
    margin: 0 auto;
  }

  .app-error-screen__body {
    text-align: center;
  }

  .app-error-screen__actions {
    justify-content: center;
  }
}
</style>
